import { Button, CircularProgress, FormControl, InputLabel, Pagination, Stack, Table, TableBody, TableCell, TableHead, TableRow, Select, MenuItem, SelectChangeEvent, TextField, Typography } from "@mui/material";
import BetlistShortDto from "../models/dtos/BetlistShortDto";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { getBetlistsPaginated, updateBetStateForBetlist } from "../Api";
import LiveBetBadge from "./LiveBetBadge";

interface BetlistsTableProps {
    userId: number
}

const BETLIST_STATE_FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'won', label: 'Won' },
    { value: 'lost', label: 'Lost' },
    { value: 'pending', label: 'Pending' },
    { value: 'sold', label: 'Sold (cashed out)' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'returned', label: 'Returned' },
];

const STATE_RETURNED = 1;
const STATE_PENDING = 2;
const STATE_WON = 4;
const STATE_LOST = 8;
const STATE_SOLD = 16;
const STATE_CANCELLED = 32;

const BET_STATES: Record<number, { text: string, background: string }> = {
    [STATE_RETURNED]: { text: 'RETURNED', background: 'lightBlue' },
    [STATE_PENDING]: { text: 'PENDING', background: 'orange' },
    [STATE_WON]: { text: 'WON', background: 'lightGreen' },
    [STATE_LOST]: { text: 'LOST', background: '#FFCCCB' },
    [STATE_SOLD]: { text: 'SOLD', background: '#E1BEE7' },
    [STATE_CANCELLED]: { text: 'CANCELLED', background: '#E0E0E0' },
};

const getBetlistBackground = (state: number) => BET_STATES[state]?.background ?? '#E0E0E0';

const getBetlistStateText = (state: number) => BET_STATES[state]?.text ?? `UNKNOWN (${state})`;

/** Sold and cancelled betslips were already paid out to the player, so their bets can't be re-settled. */
const isBetlistLocked = (state: number) => state === STATE_SOLD || state === STATE_CANCELLED;

const finishedLabel = (state: number) => {
    switch (state) {
        case STATE_SOLD: return 'Sold';
        case STATE_CANCELLED: return 'Cancelled';
        default: return 'Settled';
    }
}

const betlistMoneySummary = (item: BetlistShortDto) => {
    switch (item.state) {
        case STATE_SOLD: {
            const cashOutCoef = item.moneyPlaced > 0 ? (item.moneyReturned / item.moneyPlaced).toFixed(2) : '–';
            return `Sold for ${item.moneyReturned} of ${item.moneyPlaced} staked (×${cashOutCoef})`;
        }
        case STATE_CANCELLED:
            return `Refunded ${item.moneyReturned} of ${item.moneyPlaced} staked`;
        default:
            return `${item.moneyReturned} / ${item.moneyPlaced} 🤑`;
    }
}

interface BetlistQuery {
    stateFilter: string
    betlistId: number | null
}

export default function BetlistsTable(props: BetlistsTableProps) {

    const [state, setState] = React.useState({
        betlists: [] as BetlistShortDto[],
        loaded: false,
        loadError: false,
        page: 1,
        totalPages: 1,
        totalItems: 0,
        pageSize: 100,
        stateFilter: 'all',
        betlistId: null as number | null,
        betlistIdInput: '',
        betlistIdError: false,
    });

    const latestRequest = React.useRef(0);

    const currentQuery = (): BetlistQuery => ({ stateFilter: state.stateFilter, betlistId: state.betlistId });

    const fetchData = async (page = 1, query: BetlistQuery = currentQuery()) => {
        const requestId = ++latestRequest.current;
        setState(prevState => ({ ...prevState, ...query, loaded: false, loadError: false }));
        const offset = (page - 1) * state.pageSize;
        const limit = state.pageSize;

        const betlistsResponse = await getBetlistsPaginated(props.userId, offset, limit, query.stateFilter, query.betlistId ?? undefined);
        if (requestId !== latestRequest.current) return;
        if (betlistsResponse) {
            const totalPages = Math.ceil(betlistsResponse.totalItems / state.pageSize);
            setState(prevState => ({
                ...prevState,
                betlists: betlistsResponse.items ?? [],
                loaded: true,
                totalPages: totalPages,
                totalItems: betlistsResponse.totalItems,
                page: page
            }));
        } else {
            setState(prevState => ({ ...prevState, betlists: [], totalPages: 0, totalItems: 0, loaded: true, loadError: true }));
        }
    }

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchData(page);
    };

    const handlePageSelectChange = (event: SelectChangeEvent<number>) => {
        const selectedPage = Number(event.target.value);
        fetchData(selectedPage);
    };

    const handleStateFilterChange = (event: SelectChangeEvent<string>) => {
        fetchData(1, { ...currentQuery(), stateFilter: event.target.value });
    };

    const searchBetlistId = () => {
        const raw = state.betlistIdInput.trim();
        if (raw === '') {
            clearBetlistSearch();
            return;
        }
        if (!/^\d+$/.test(raw)) {
            setState(prevState => ({ ...prevState, betlistIdError: true }));
            return;
        }
        setState(prevState => ({ ...prevState, betlistIdError: false }));
        fetchData(1, { ...currentQuery(), betlistId: Number(raw) });
    };

    const clearBetlistSearch = () => {
        setState(prevState => ({ ...prevState, betlistIdInput: '', betlistIdError: false }));
        fetchData(1, { ...currentQuery(), betlistId: null });
    };

    const stateFilterLabel = BETLIST_STATE_FILTERS.find(f => f.value === state.stateFilter)?.label ?? 'All';

    const resultSummary = () => {
        if (state.loadError) return 'Could not load betslips.';
        if (state.betlistId != null) {
            if (state.totalItems > 0) return `Showing betslip #${state.betlistId}.`;
            return state.stateFilter === 'all'
                ? `Betslip #${state.betlistId} was not found for this user.`
                : `Betslip #${state.betlistId} was not found for this user with state ${stateFilterLabel}. Try All.`;
        }
        return `${state.totalItems} betslip${state.totalItems === 1 ? '' : 's'} found.`;
    };

    const updateBetState = async (betlistId: number, betId: number, betState: number) => {
        let betlist = await updateBetStateForBetlist(props.userId, betlistId, betId, betState);
        setState(prevState => ({ ...prevState, toastMsg: betlist != null ? "Updated betlist" : "Something went wrong!" }));
        if (betlist) {
            let existingBetlist = state.betlists.find(b => b.betlistId == betlistId);
            if (existingBetlist) {
                let index = state.betlists.indexOf(existingBetlist);
                state.betlists[index] = betlist;
                setState(prevState => ({ ...prevState, betlists: state.betlists }));
            }
        }
    }

    const navigate = useNavigate();

    React.useEffect(() => {
        fetchData(1);
    }, []);

    return (
        <>
            <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ width: '100%', mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="betlist-state-filter-label">State</InputLabel>
                    <Select
                        labelId="betlist-state-filter-label"
                        label="State"
                        value={state.stateFilter}
                        onChange={handleStateFilterChange}
                    >
                        {BETLIST_STATE_FILTERS.map(f => (
                            <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Betslip ID"
                    size="small"
                    value={state.betlistIdInput}
                    error={state.betlistIdError}
                    helperText={state.betlistIdError ? 'Enter a numeric betslip id' : undefined}
                    onChange={e => setState(prevState => ({ ...prevState, betlistIdInput: e.target.value, betlistIdError: false }))}
                    onKeyDown={e => {
                        if (e.key === 'Enter') searchBetlistId();
                    }}
                    sx={{ width: 220 }}
                />
                <Button variant="contained" onClick={searchBetlistId} sx={{ height: 40 }}>Search</Button>
                {state.betlistId != null ? (
                    <Button variant="outlined" onClick={clearBetlistSearch} sx={{ height: 40 }}>Clear</Button>
                ) : null}
            </Stack>
            {state.loaded ? (
                <Typography variant="body2" color={state.loadError ? 'error' : 'text.secondary'} sx={{ alignSelf: 'flex-start', mb: 1 }}>
                    {resultSummary()}
                </Typography>
            ) : null}
            {!state.loaded ? (
                <CircularProgress size='100px' style={{ marginBottom: '32px' }} /> 
            ) : (
                <>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Id</TableCell>
                                <TableCell align="left">Placed At / Bet name</TableCell>
                                <TableCell align="left">Match</TableCell>
                                <TableCell align="left">Variety</TableCell>
                                <TableCell align="left">State</TableCell>
                                <TableCell align="left">Coef</TableCell>
                                <TableCell align="left">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {state.betlists.map(item => (
                                <React.Fragment key={item.betlistId}>
                                    <TableRow>
                                        <TableCell  sx={{ background: getBetlistBackground(item.state) }}>
                                            {item.betlistId}
                                        </TableCell>
                                        <TableCell colSpan={3} sx={{ background: getBetlistBackground(item.state) }}>
                                            {new Date(item.placedAt).toLocaleString()}
                                            {item.finishedAt && item.state !== STATE_PENDING ? (
                                                <div style={{ fontSize: '0.75rem', color: '#616161' }}>
                                                    {finishedLabel(item.state)} {new Date(item.finishedAt).toLocaleString()}
                                                </div>
                                            ) : null}
                                        </TableCell>
                                        <TableCell sx={{ background: getBetlistBackground(item.state) }}>
                                            <b>{getBetlistStateText(item.state)}</b>
                                            <div>{betlistMoneySummary(item)}</div>
                                        </TableCell>
                                        <TableCell colSpan={2} sx={{ background: getBetlistBackground(item.state) }}>
                                            <b>{item.finalCoefficient}</b>
                                        </TableCell>
                                    </TableRow>
                                    {item.bets.map(bet => (
                                        <TableRow key={bet.id}>
                                            <TableCell></TableCell>
                                            <TableCell>
                                                {bet.betGroupName && <div style={{ fontSize: '0.7rem', color: '#9e9e9e', lineHeight: 1.2 }}>{bet.betGroupName}</div>}
                                                {bet.name}
                                                <LiveBetBadge live={bet.setOnLive} />
                                            </TableCell>
                                            <TableCell><Link to={'/matches/'+bet.matchId}>{bet.homeTeamName} vs {bet.awayTeamName}</Link>&nbsp;({new Date(bet.date).toLocaleString()})</TableCell>
                                            <TableCell>{bet.period}</TableCell>
                                            <TableCell title={bet.state === STATE_SOLD ? 'Still open when the betslip was sold' : undefined}>
                                                {getBetlistStateText(bet.state)}
                                            </TableCell>
                                            <TableCell>{bet.coefficient}</TableCell>
                                            <TableCell>
                                                {isBetlistLocked(item.state) ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Locked: betslip {getBetlistStateText(item.state).toLowerCase()}
                                                    </Typography>
                                                ) : (
                                                    <>
                                                        <Button variant={bet.state == STATE_WON ? 'contained' : 'outlined'} style={{ height: '54px' }} color='success' onClick={() => updateBetState(item.betlistId, bet.id, STATE_WON)}>w</Button>
                                                        <Button variant={bet.state == STATE_LOST ? 'contained' : 'outlined'} style={{ height: '54px' }} color='error' onClick={() => updateBetState(item.betlistId, bet.id, STATE_LOST)}>L</Button>
                                                        <Button variant={bet.state == STATE_RETURNED ? 'contained' : 'outlined'} style={{ height: '54px' }} color='info' onClick={() => updateBetState(item.betlistId, bet.id, STATE_RETURNED)}>R</Button>
                                                        <Button variant={bet.state == STATE_PENDING ? 'contained' : 'outlined'} style={{ height: '54px' }} color='warning' onClick={() => updateBetState(item.betlistId, bet.id, STATE_PENDING)}>P</Button>
                                                    </>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                    {state.totalPages > 0 ? (
                    <Stack 
                        direction="row" 
                        spacing={2} 
                        sx={{ mt: 3, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <Pagination 
                            count={state.totalPages} 
                            page={state.page} 
                            onChange={handlePageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                        />
                        <Select
                            value={state.page}
                            onChange={handlePageSelectChange}
                            label="Navigate to ..."
                            style={{ width: '150px' }}
                        >
                            {Array.from({ length: state.totalPages }, (_, i) => i + 1).map(page => (
                                <MenuItem key={page} value={page}>
                                    Page {page}
                                </MenuItem>
                            ))}
                        </Select>
                    </Stack>
                    ) : null}
                </>
            )}
        </>
    );
}